// Theme-aware ApexCharts helpers so every chart switches cleanly between light
// and dark and shares one palette. Usage in a component:
//
//   import { useChartBase, useChartPalette, mergeChartOptions } from "_shared/ui/chartTheme";
//   const base = useChartBase();
//   const palette = useChartPalette();
//   const options = mergeChartOptions(base, {
//     labels: ["Planned", "Unplanned"],
//     colors: palette,           // or omit to inherit theme palette
//   });
//   <Chart type="donut" series={series} options={options} />

import { useTheme } from "@mui/material/styles";

// Deep-merge plain objects (arrays and primitives from `override` win). Needed
// because ApexCharts option objects are deeply nested — a shallow spread would
// clobber base keys like chart.foreColor when a chart sets chart.width.
export const mergeChartOptions = (base, override = {}) => {
  const out = { ...base };
  Object.keys(override).forEach((key) => {
    const b = base[key];
    const o = override[key];
    if (
      b && o &&
      typeof b === "object" && typeof o === "object" &&
      !Array.isArray(b) && !Array.isArray(o)
    ) {
      out[key] = mergeChartOptions(b, o);
    } else {
      out[key] = o;
    }
  });
  return out;
};

// The consistent categorical series palette (matches StatCard/theme semantics).
export const useChartPalette = () => {
  const theme = useTheme();
  const p = theme.palette;
  return [
    p.primary.main,
    p.info.main,
    p.success.main,
    p.warning.main,
    p.secondary.main,
    p.error.main,
  ];
};

// Base ApexCharts options that make text, grid, tooltip, and legend theme-aware.
// Spread/merge these into any chart's options via mergeChartOptions().
export const useChartBase = () => {
  const theme = useTheme();
  const p = theme.palette;
  const palette = [
    p.primary.main,
    p.info.main,
    p.success.main,
    p.warning.main,
    p.secondary.main,
    p.error.main,
  ];
  return {
    colors: palette,
    chart: {
      foreColor: p.text.secondary,
      fontFamily: theme.typography.fontFamily,
      toolbar: { show: false },
      background: "transparent",
    },
    theme: { mode: p.mode },
    // Horizontal grid ("row") lines behind bars/lines for readability.
    grid: {
      show: true,
      borderColor: p.divider,
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    tooltip: { theme: p.mode },
    legend: { labels: { colors: p.text.secondary } },
    stroke: { colors: [p.background.paper] },
    dataLabels: { style: { colors: [p.text.primary] } },
    xaxis: { labels: { style: { colors: p.text.secondary } } },
    yaxis: { labels: { style: { colors: p.text.secondary } } },
  };
};
